
import {
  Component, Vue,
} from 'nuxt-property-decorator'
import GptLog from './GptLog.vue'
import GptWelcome from './GptWelcome.vue'
import BaseIconButton from '../base/BaseIconButton.vue'
import { globalLabelAsString } from '../../shared/general/services/StoreService'
import { TGptChatResponse, TGptLog, TGptMessage } from '../../shared/general/types/GptHub'
import { Logger } from '../../shared/general/logger/Logger'
import { nuxt } from '../../shared/general/logger/LogKey'
import GptConversationStarters from './GptConversationStarters.vue'

@Component({
  name: 'GptHubIntegration',
  components: {
    BaseIconButton,
    GptConversationStarters,
    GptLog,
    GptWelcome,
  },
})
export default class GptHubIntegration extends Vue {
  private log : TGptMessage[] = []

  private input : string = ''

  private loading : boolean = false

  private failed : boolean = false

  private conversationStarters : string[] = [
    'How do I download the Disprz app?',
    'How do I prepare for the training?',
    'How do I verify my identity?',
    'How do you navigate to the e-learning courses (self-directed)?',
  ]

  private get gptHubInputLabel () : string {
    return globalLabelAsString('gpt_hub_input_label')
  }

  private get gptHubHintLabel () : string {
    return globalLabelAsString('gpt_hub_hint_label')
  }

  private get filteredLog () : TGptMessage[] {
    return this.log.filter((entry) => entry.role !== 'developer')
  }

  private async send () : Promise<void> {
    if (!this.input) return

    const message : TGptMessage = { role: 'user', content: this.input }

    this.log = [...this.log, message]
    this.input = ''

    await this.callGptHub()
  }

  private async resend () : Promise<void> {
    await this.callGptHub()
  }

  private async callGptHub () : Promise<void> {
    try {
      this.loading = true
      this.failed = false

      const body : TGptLog = { messages: this.log }
      const response = await fetch(
        '/api/gptHub/chat',
        {
          method: 'POST',
          headers: {
            Accept: 'text/event-stream',
            'Cache-Control': 'no-store, no-transform',
            'Content-Type': 'application/json',
            'X-No-Compression': 'true',
          },
          body: JSON.stringify(body),
        },
      )

      if (!response.ok || !response.body) {
        this.failed = true
        return
      }

      const reader = response.body.getReader()
      const message : TGptMessage = { role: 'assistant', content: '' }

      // eslint-disable-next-line no-constant-condition
      for (let i = 0; true; i += 1) {
        // eslint-disable-next-line no-await-in-loop
        const { done, value } = await reader.read()

        if (done) break

        message.content += this.parseMessage(Buffer.from(value).toString())
        this.log = i === 0 ? [...this.log, message] : [...this.log]
      }
    } catch (error) {
      Logger.error(nuxt, 'Health check failed for GPT Hub', error)
      this.failed = true
    } finally {
      this.loading = false
    }
  }

  private async conversationStarterClick (input : string) {
    if (!input.length) return

    const message : TGptMessage = { role: 'user', content: input }
    this.log = [...this.log, message]
    await this.callGptHub()
  }

  private refreshConversationStarters () : void {
    this.conversationStarters.sort(() => 0.5 - Math.random())
  }

  private parseMessage (input : string) : string {
    return input.split(/\r?\n/)
      .map((line) => line.trim().replace(/^\s*data:\s*/g, ''))
      .filter((line) => line.length > 0)
      .map((line) => JSON.parse(line) as TGptChatResponse)
      .filter((response) => response.id)
      .map((response) => response.content)
      .join('')
  }
}
